import { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { dequal } from 'dequal';

// Types
import type { AxiosError } from 'axios';
import type {
    DataApiDateType,
    DataApiType,
    UseGroupGlobalTableAnalyticsProps,
    DataGroupGlobalTableAnalyticsType,
    GroupGlobalTableAnalyticsFieldsType
} from '@/utils/hooks/swr/group/useGroupGlobalTableAnalytics/types';

// Utils
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Hooks
import useSPGroups from '@/hooks/swr/group/useSPGroups';
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';

// API Routes
import { GROUP_ANALYTICS } from '@/routes/apiEndpoints';

const useGroupGlobalTableAnalytics = <TFields extends GroupGlobalTableAnalyticsFieldsType[]>({
    groupId,
    params,
    options
}: UseGroupGlobalTableAnalyticsProps<TFields>) => {
    const firstLoad = useRef(null);
    const [groupAnalytics, setGroupAnalytics] =
        useState<Prettify<DataGroupGlobalTableAnalyticsType<TFields>>>(null);
    const [shouldReFetchGroupAnalytics, setShouldReFetchGroupAnalytics] = useState(false);
    const [comparisonDates, setComparisonDates] = useState<{
        current: DataApiDateType;
        previous: DataApiDateType;
    }>(null);

    const { socialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { spGroups } = useSPGroups({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    if (params.period !== 'custom') {
        delete params?.start;
        delete params?.end;
    }

    const [requestParams, setRequestParams] = useState(params);
    const isParamsIdentical = dequal(params, requestParams);

    const {
        data,
        mutate: mutateGroupAnalytics,
        isValidating: isValidatingGroupAnalytics,
        error: groupAnalyticsError
    } = useSWR<DataGroupGlobalTableAnalyticsType<TFields>, AxiosError<Error>>(
        requestParams &&
            Boolean(requestParams?.fields?.length) &&
            (isParamsIdentical || shouldReFetchGroupAnalytics)
            ? [
                  injectEndpointUrlParams(GROUP_ANALYTICS, { GROUP_ID: groupId }),
                  ...Object.values(requestParams)
              ]
            : null,
        ([url]) =>
            get(url, requestParams).then(
                (res: {
                    data: {
                        analytics: DataApiType<TFields>;
                        dates: { current: DataApiDateType; previous: DataApiDateType };
                    };
                }) => {
                    setComparisonDates(res.data.dates);
                    return formatData(res.data.analytics);
                }
            ),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    // Reorder data to have group at the top
    const getReOrderedData = () => {
        if (!data || (data && data.length && 'group' in data[0])) return data;

        const analyticsData = data;
        // Find the index of the element with the "group" key in the array
        const groupIndex = analyticsData.findIndex((item) => 'group' in item);

        if (groupIndex !== -1) {
            // Remove the element with the "group" key from the array and store
            const groupItem = analyticsData.splice(groupIndex, 1)[0];

            // Place the groupItem element at the beginning of the array
            analyticsData.unshift(groupItem);
        }

        return analyticsData;
    };

    // Format data to replace id of "group" and "social_profile" by the object
    const formatData = (dataApi: DataApiType<TFields>) => {
        return dataApi.map((item) => {
            if ('group' in item) {
                const group = spGroups?.find((group) => group.id === item.group);
                return {
                    ...item,
                    group
                };
            }
            if ('social_profile' in item && typeof item.social_profile === 'number') {
                const socialProfile = socialProfiles?.find(
                    (profile) => profile.id === item.social_profile
                );
                return {
                    ...item,
                    social_profile: socialProfile
                };
            }
        }) as unknown as DataGroupGlobalTableAnalyticsType<TFields>;
    };

    useEffect(() => {
        if (isParamsIdentical) {
            // switch back shouldRefresh to false
            if (shouldReFetchGroupAnalytics) return setShouldReFetchGroupAnalytics(false);
            return;
        }

        setRequestParams(params);
        firstLoad.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, params.period, params.start, params.end, params.comparison_period]);

    useEffect(() => {
        if (isParamsIdentical) {
            // switch back shouldRefresh to false
            if (shouldReFetchGroupAnalytics) return setShouldReFetchGroupAnalytics(false);
            return;
        }
        setRequestParams(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.fields]);

    useEffect(() => {
        if (!data && !groupAnalyticsError) {
            return;
        }
        firstLoad.current = true;
        setGroupAnalytics(getReOrderedData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupAnalyticsError, data]);

    return {
        groupAnalytics,
        comparisonDates,
        mutateGroupAnalytics,
        groupAnalyticsError,
        setGroupAnalytics,
        isValidatingGroupAnalytics,
        isLoading: (!groupAnalytics && !groupAnalyticsError) || !firstLoad.current
    };
};
export default useGroupGlobalTableAnalytics;
